/*
 * decaffeinate suggestions:
 * DS207: Consider shorter variations of null checks
 * Full docs: https://github.com/decaffeinate/decaffeinate/blob/master/docs/suggestions.md
 */
import {datadogLogs, StatusType} from '@datadog/browser-logs';
import {captureException} from '@sentry/browser';
import {ScopeContext} from '@sentry/types';
import _ from 'underscore';

import * as browser from 'lib/Browser';
import * as net from 'util/Net';

enum Level {
  Warning = 'warning',
  Error = 'error',
  Info = 'info',
}

const levelToStatus: Record<Level, StatusType> = {
  [Level.Info]: StatusType.info,
  [Level.Warning]: StatusType.warn,
  [Level.Error]: StatusType.error,
};

function log(msg: string | Error, level: Level, scope?: Partial<ScopeContext>): void {
  if (scope == null) {
    scope = {};
  }
  // Note that all console logging methods are automatically captured as raven breadcrumbs.
  switch (level) {
    case Level.Warning:
      console.warn(msg, scope); // oxlint-disable-line no-console
      break;
    case Level.Error:
      console.error(msg, scope); // oxlint-disable-line no-console
      break;
    case Level.Info:
      console.info(msg, scope); // oxlint-disable-line no-console
      break;
    default:
      throw new Error(`Unexpected level: ${level}`);
  }
  if (!browser.isBrowser) {
    return;
  }

  if (typeof msg === 'string') {
    datadogLogs.logger.log(msg, scope, levelToStatus[level]);
  } else {
    datadogLogs.logger.log('Error occurred', scope, levelToStatus[level], msg);
  }

  if (!(msg instanceof Error)) {
    // Wrap msg in Error so that stacktrace is captured
    msg = new Error(msg);
  }
  if (level === Level.Warning || level === Level.Error) {
    // eslint-disable-next-line no-restricted-syntax
    captureException(msg, _.extend({level}, scope));
  }
}

function warn(msg: string | Error, scope?: Partial<ScopeContext>): void {
  log(msg, Level.Warning, scope);
}

function error(msg: string | Error, scope?: Partial<ScopeContext>): void {
  log(msg, Level.Error, scope);
}

function info(msg: string | Error, scope?: Partial<ScopeContext>): void {
  log(msg, Level.Info, scope);
}

function exception(msg: string | Error, scope?: Partial<ScopeContext>): void {
  if (scope == null) {
    scope = {};
  }
  log(msg, scope.level != null ? (scope.level as Level) : Level.Error, scope);
}

export interface DumpOptions {
  // Large data to email.
  data?: object;
  // Data sent to both sentry / email.
  extra?: object;
}

/**
 * Log large data to a file in S3 and email it. Useful for when Sentry would truncate the data.
 *
 * @param module - a namespace for the dump for easier triaging
 * @param error - reason for logging the data
 * @param options
 */
function dump(module: string, error: string | Error, options: DumpOptions): void {
  let errorMessage;
  if (options == null) {
    options = {};
  }
  const {data, extra} = options;
  if (!(error instanceof Error)) {
    errorMessage = `${module}: ${error != null ? error : 'error'}`;
  } else {
    errorMessage = `${module}: ${error.message}`;
  }
  net.postJSON({
    url: '/1/api/debug/client-dump',
    data: {
      currentLocation: document.location.href,
      module,
      // eslint-disable-next-line no-restricted-syntax
      extra: _.extend({}, extra, data),
      error: errorMessage,
    },
  });
}

export const LEVEL_WARNING = Level.Warning;
export const LEVEL_ERROR = Level.Error;
export {warn, error, info, exception, dump};
